import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, Plus, RefreshCw, Search, Sparkles, Upload } from 'lucide-react'
import clsx from 'clsx'

import { withDemo } from '@/config/demoMode'
import { DEMO_CRM_CONTACTS } from '@/data/crmDemoContacts'
import { crmListService, crmService, isCrmNotFound, type CrmContact } from '@/services/crm'
import { SMART_LIST_KEYS, crmRelationshipService, type SmartListKey } from '@/services/crmRelationships'
import ActivityView from '@/components/crm/ActivityView'
import AddContactDialog from '@/components/crm/AddContactDialog'
import BrokerBulkImportDialog from '@/components/crm/BrokerBulkImportDialog'
import BrokerReferenceWorkspace from '@/components/crm/BrokerReferenceWorkspace'
import ContactProfilePanel from '@/components/crm/ContactProfilePanel'
import ContactTable from '@/components/crm/ContactTable'
import { ColumnPicker, useContactColumns } from '@/components/crm/contactColumns'
import { DensityToggle, useContactDensity } from '@/components/crm/contactDensity'
import ContactWindow from '@/components/crm/ContactWindow'
import EditContactDialog from '@/components/crm/EditContactDialog'
import { captureHadFailure, captureSentence, matchesSearch } from '@/components/crm/crmFormat'
import RelationshipBoard, { type BoardEntry } from '@/components/crm/relationships/RelationshipBoard'
import RelationshipPeek from '@/components/crm/relationships/RelationshipPeek'
import SmartListRail from '@/components/crm/relationships/SmartListRail'
import TodayStrip from '@/components/crm/relationships/TodayStrip'
import { TYPE_CHIPS, matchesTypeChip, rowsById, type TypeChipKey } from '@/components/crm/relationships/relationshipFormat'
import { useCrmLayout, useTodayStripCollapsed, type CrmLayout } from '@/components/crm/relationships/useCrmLayout'

/**
 * The CRM: one workspace over every party the platform holds.
 *
 * From the top: the Today strip (four counts, each with a way into the list
 * behind it); the left rail (lists that update themselves, then the desk's own
 * saved lists); the toolbar (type chips and the Table | Board switch); and the
 * view itself. Table is the default and is the directory exactly as it was,
 * with Stage, Health and 12-month columns added where the server has data for
 * them; Board lays the same people out by relationship stage.
 *
 * The page stays thin. It reads the directory, the lists and the relationship
 * rows, keeps the URL honest (`?focus=` a contact, `?open=1` its window,
 * `?view=activity` the enrichment surface, `?list=` a saved list, `?smart=` a
 * built-in list, `?layout=` board or table for one visit), and composes the
 * pieces. Every fact on screen is a count of what the server returned or a
 * field on a record it returned; the sample roster is an overlay for sales
 * demos and is appended only under `withDemo`, after the real rows.
 *
 * Nothing here adapts the server's shape into a page-local one. The old page
 * kept its own contact type and a `fromApi` reshaping step, and that step is
 * where notes, company ids and captured phone slots were dropped on the way to
 * the dialog that edited them.
 */

const CAPTURE_TOOLTIP =
  'Capture runs on every message and deal automatically; this re-reads everything already on the books and runs dedup.'

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

/** The stat tiles above the directory: what each counts, and the filter it
 *  applies when a person clicks it. Kept as one table so the number shown and
 *  the rows revealed can never drift apart. */
type StatKey = 'all' | 'withDeals' | 'needsReview' | 'enriched'

const STAT_TILES: { key: StatKey; label: string }[] = [
  { key: 'all', label: 'Contacts' },
  { key: 'withDeals', label: 'With deals' },
  { key: 'needsReview', label: 'Needs review' },
  { key: 'enriched', label: 'Enriched' },
]

const STAT_PREDICATES: Record<StatKey, (c: CrmContact) => boolean> = {
  all: () => true,
  withDeals: (c) => c.linkedDeals.length > 0,
  needsReview: (c) => Boolean(c.needsReview),
  enriched: (c) => Boolean(c.enrichment?.lookedUpAt),
}

const TOOLBAR_BUTTON =
  'flex items-center gap-1 whitespace-nowrap rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1 text-[11px] font-medium text-stone-300 transition-colors hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:text-stone-600'

function isSmartKey(value: string | null): value is SmartListKey {
  return value !== null && (SMART_LIST_KEYS as readonly string[]).includes(value)
}

export default function CRMDatabase() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeListId = searchParams.get('list')
  const smartParam = searchParams.get('smart')
  const activeSmart: SmartListKey | null = isSmartKey(smartParam) ? smartParam : null
  const view = searchParams.get('view')
  const focusId = searchParams.get('focus')
  const openOnFocus = searchParams.get('open') === '1'

  const [layout, setLayout] = useCrmLayout(searchParams.get('layout'))
  const [todayCollapsed, setTodayCollapsed] = useTodayStripCollapsed()
  const [typeChip, setTypeChip] = useState<TypeChipKey>('all')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [windowId, setWindowId] = useState<string | null>(null)
  const [editing, setEditing] = useState<CrmContact | null>(null)
  const [addingContact, setAddingContact] = useState(false)
  const [importing, setImporting] = useState(false)
  const [includeRetired, setIncludeRetired] = useState(false)
  const [captureNote, setCaptureNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [focusNote, setFocusNote] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // The real roster: brokers, their firms, sponsors and the contacts captured
  // off the deals, as the platform holds them.
  const contactQuery = useQuery({
    queryKey: ['crm', 'contacts', { includeRetired }],
    queryFn: async () => {
      try {
        const body = await crmService.getContacts({ includeRetired })
        return {
          ...body,
          contacts: Array.isArray(body?.contacts) ? body.contacts : [],
        }
      } catch (error) {
        // An unknown list or a partition that holds nobody is empty, not unread.
        if (isCrmNotFound(error)) {
          return { contacts: [], total: 0, sourcedFrom: [], capturedFromDeals: 0 }
        }
        throw error
      }
    },
    staleTime: 60_000,
    retry: false,
  })

  const contacts = useMemo<CrmContact[]>(() => {
    const live = contactQuery.data?.contacts ?? []
    // Demo data is an overlay for sales demos, never a substitute: real
    // records always lead, and the sample roster is appended only when the
    // demo switch is on.
    return [...live, ...withDemo(DEMO_CRM_CONTACTS, [], 'crm')]
  }, [contactQuery.data])

  // Each contact's stage, health and activity, and the built-in lists, derived
  // by the server on every read. Keyed under the roster's prefix so every
  // existing invalidation of the roster (a confirm, a retire, a merge, a
  // re-read) refreshes it too.
  const relationships = useQuery({
    queryKey: ['crm', 'contacts', 'relationships'],
    queryFn: crmRelationshipService.get,
    staleTime: 60_000,
    retry: false,
  })
  const relationshipRows = useMemo(() => rowsById(relationships.data?.rows), [relationships.data])

  // Pairs the matcher could not settle: the same query the review screen reads,
  // so opening it costs nothing more.
  const duplicates = useQuery({
    queryKey: ['crm', 'duplicates'],
    queryFn: () => crmService.getDuplicates(),
    staleTime: 60_000,
    retry: false,
  })

  // The chips come from the same query the rail reads, so the active chip's
  // name is known here for the empty-state sentence.
  const lists = useQuery({
    queryKey: ['crm', 'lists'],
    queryFn: crmListService.list,
    staleTime: 30_000,
  })
  const activeList = useMemo(
    () => (activeListId ? lists.data?.lists.find((l) => l.id === activeListId) ?? null : null),
    [lists.data, activeListId],
  )
  const activeSmartList = useMemo(
    () => (activeSmart ? relationships.data?.lists.find((l) => l.key === activeSmart) ?? null : null),
    [relationships.data, activeSmart],
  )

  // Who is on the selected list, from the server's own evaluation of its
  // rules — so the table and the chip's own count can never disagree.
  const members = useQuery({
    queryKey: ['crm', 'lists', activeListId, 'members'],
    queryFn: async () => {
      try {
        const body = await crmListService.members(activeListId as string)
        return {
          ...body,
          contacts: Array.isArray(body?.contacts) ? body.contacts : [],
        }
      } catch (error) {
        // A list id from another partition (or a deleted chip) is empty, not a
        // directory read failure. The table stays usable.
        if (isCrmNotFound(error)) {
          return { id: activeListId as string, name: '', total: 0, contacts: [], missing: true }
        }
        throw error
      }
    },
    enabled: Boolean(activeListId),
    staleTime: 30_000,
    retry: false,
  })
  const smartFailed = Boolean(activeSmart && relationships.isError)
  const listReady =
    (!activeListId || members.isSuccess || members.isError) &&
    (!activeSmart || relationships.isSuccess || relationships.isError)
  const membersFailed = Boolean(activeListId && members.isError && !isCrmNotFound(members.error))

  // Which stat tile is acting as a filter. The tiles are the fastest way into
  // the subset they count, so clicking one narrows the table to exactly the
  // rows behind that number; clicking it again clears it.
  const [statFilter, setStatFilter] = useState<StatKey | null>(null)
  const [tableColumns, setTableColumns] = useContactColumns()
  const [density, setDensity] = useContactDensity()

  // The list + type + search scope. The stat tiles count this, so their
  // numbers stay put when one of them is used as a filter — a count that
  // collapsed to itself the moment you clicked it would be useless as a way
  // back.
  const scoped = useMemo(() => {
    let rows = contacts
    if (activeListId && members.data && !members.data.missing) {
      const onList = new Set(members.data.contacts.map((c) => c.id))
      rows = rows.filter((c) => onList.has(c.id))
    }
    if (activeSmart && relationships.data) {
      rows = rows.filter((c) => relationshipRows.get(c.id)?.lists.includes(activeSmart))
    }
    if (typeChip !== 'all') rows = rows.filter((c) => matchesTypeChip(c, typeChip))
    if (search.trim()) rows = rows.filter((c) => matchesSearch(c, search))
    return rows
  }, [contacts, activeListId, members.data, activeSmart, relationships.data, relationshipRows, typeChip, search])

  // A hidden filter must never silently narrow a view that does not show it:
  // the stat tiles live on the table, so they apply to the table alone.
  const filtered = useMemo(
    () => (statFilter && layout === 'table' ? scoped.filter(STAT_PREDICATES[statFilter]) : scoped),
    [scoped, statFilter, layout],
  )

  const boardEntries = useMemo<BoardEntry[]>(
    () =>
      scoped.flatMap((contact) => {
        const row = relationshipRows.get(contact.id)
        return row && row.stage ? [{ contact, row }] : []
      }),
    [scoped, relationshipRows],
  )

  const selected = useMemo(() => contacts.find((c) => c.id === selectedId) ?? null, [contacts, selectedId])
  const windowContact = useMemo(() => contacts.find((c) => c.id === windowId) ?? null, [contacts, windowId])

  // A selection that has left the view — filtered out, retired with the
  // toggle off — is not kept as a panel over rows it is not among.
  useEffect(() => {
    if (!selectedId || !listReady || contactQuery.isLoading) return
    if (!filtered.some((c) => c.id === selectedId)) setSelectedId(null)
  }, [selectedId, filtered, listReady, contactQuery.isLoading])

  // URL helpers
  const updateParams = useCallback(
    (mutate: (next: URLSearchParams) => void, replace = true) => {
      const next = new URLSearchParams(searchParams)
      mutate(next)
      setSearchParams(next, { replace })
    },
    [searchParams, setSearchParams],
  )
  const setParam = useCallback(
    (key: string, value: string | null, replace = true) =>
      updateParams((next) => {
        if (value === null) next.delete(key)
        else next.set(key, value)
      }, replace),
    [updateParams],
  )

  // A saved list and a built-in list are one choice of scope, never two.
  const selectSavedList = useCallback(
    (id: string | null) =>
      updateParams((next) => {
        next.delete('smart')
        if (id) next.set('list', id)
        else next.delete('list')
      }),
    [updateParams],
  )
  const selectSmartList = useCallback(
    (key: SmartListKey | null) =>
      updateParams((next) => {
        next.delete('list')
        if (key) next.set('smart', key)
        else next.delete('smart')
      }),
    [updateParams],
  )
  const openSmartList = useCallback(
    (key: SmartListKey) => {
      setSearch('')
      setTypeChip('all')
      setStatFilter(null)
      selectSmartList(key)
    },
    [selectSmartList],
  )

  // Deep link: /crm?focus=<id> selects the row and opens the panel;
  // ?open=1 opens the window too. Consumed once the roster can answer it,
  // and cleared so a reload does not re-open it, mirroring Intake.
  useEffect(() => {
    if (!focusId || !contactQuery.isSuccess) return
    const found = contacts.some((c) => c.id === focusId)
    if (found) {
      setSearch('')
      setTypeChip('all')
      setSelectedId(focusId)
      if (openOnFocus) setWindowId(focusId)
      setFocusNote(null)
    } else {
      setFocusNote(`No contact ${focusId} is in the directory — it may have been retired or merged.`)
    }
    const next = new URLSearchParams(searchParams)
    next.delete('focus')
    next.delete('open')
    if (found) {
      next.delete('list')
      next.delete('smart')
    }
    setSearchParams(next, { replace: true })
  }, [focusId, openOnFocus, contactQuery.isSuccess, contacts, searchParams, setSearchParams])

  // Keyboard: "/" or ⌘K focuses the search; Escape closes the panel when no
  // dialog or menu has already taken the key.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if ((event.key === '/' && !isTypingTarget(event.target)) || (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey))) {
        event.preventDefault()
        searchRef.current?.focus()
        searchRef.current?.select()
        return
      }
      if (event.key !== 'Escape') return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      if (windowId || addingContact || editing || importing) return
      if (isTypingTarget(event.target)) return
      setSelectedId(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [windowId, addingContact, editing, importing])

  const refreshAll = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'duplicates'] }),
    ])

  // Capture runs automatically on every message and deal that arrives. This
  // reads the correspondence that arrived before it did — without it, "every
  // contact from every deal" would quietly mean "every deal since this
  // shipped" — and runs the dedup pass over the whole directory afterwards.
  const capture = useMutation({
    mutationFn: () => crmService.captureFromDeals(),
    onSuccess: async (summary) => {
      setCaptureNote({
        tone: captureHadFailure(summary) ? 'error' : 'ok',
        text: captureSentence(summary),
      })
      await refreshAll()
    },
    onError: () => setCaptureNote({ tone: 'error', text: 'The sweep could not be run.' }),
  })

  const selectContact = useCallback((id: string | null) => {
    setSelectedId(id)
  }, [])

  const openWindow = useCallback((id: string) => {
    setSelectedId(id)
    setWindowId(id)
  }, [])

  // After a retire, restore or merge the row this selection pointed at has
  // moved or gone; the roster re-read decides what is still there.
  const onRetired = useCallback((id: string) => {
    setSelectedId((current) => (current === id ? null : current))
    setWindowId((current) => (current === id ? null : current))
  }, [])

  const showExisting = useCallback(
    (id: string) => {
      setSearch('')
      if (activeListId || activeSmart) {
        updateParams((next) => {
          next.delete('list')
          next.delete('smart')
        })
      }
      setSelectedId(id)
    },
    [activeListId, activeSmart, updateParams],
  )

  const changeLayout = useCallback(
    (next: CrmLayout) => {
      setLayout(next)
      // An explicit ?layout= was for one visit; once the person chooses, their
      // choice (remembered per user) takes over.
      if (searchParams.has('layout')) setParam('layout', null)
    },
    [setLayout, searchParams, setParam],
  )

  // Only once the roster has actually been read can "there is nobody" be
  // stated. The thinner reference workspace remains the answer for a tenant
  // that genuinely holds no parties yet — it is where they get added.
  if (contactQuery.isSuccess && contacts.length === 0 && !includeRetired) {
    return <BrokerReferenceWorkspace />
  }

  const inActivity = view === 'activity'
  const inView = scoped
  const stats = STAT_TILES.map((tile) => ({
    ...tile,
    value: inView.filter(STAT_PREDICATES[tile.key]).length,
  }))

  const liveContacts = contacts.filter((c) => !c.retired)
  const enrichment = contactQuery.isSuccess
    ? {
        enriched: liveContacts.filter(STAT_PREDICATES.enriched).length,
        total: liveContacts.length,
      }
    : null
  const smartCount = (key: SmartListKey): number | null =>
    relationships.data?.lists.find((l) => l.key === key)?.count ?? null

  const activeTile = STAT_TILES.find((t) => t.key === statFilter)
  const scopeName = activeSmartList?.label ?? activeList?.name ?? null
  const emptyMessage =
    activeTile && layout === 'table'
      ? `Nothing in view is ${activeTile.label.toLowerCase()}`
      : search.trim()
        ? `Nothing in ${scopeName ?? 'the directory'} matches “${search.trim()}”`
        : scopeName
          ? activeSmart
            ? `Nobody is on ${scopeName} right now`
            : `Nothing is on ${scopeName} yet`
          : 'No contacts in view'
  const boardEmptyMessage = search.trim()
    ? `Nobody on the board matches “${search.trim()}”`
    : scopeName
      ? `Nobody here under ${scopeName}`
      : 'Nobody here with these filters'

  const shownText =
    layout === 'board'
      ? relationships.isError
        ? `Board unavailable · ${contacts.length} in the directory`
        : `${boardEntries.length} on the board · ${contacts.length} in the directory`
      : `${filtered.length} of ${contacts.length} in the directory`

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {addingContact ? (
        <AddContactDialog onClose={() => setAddingContact(false)} onOpenExisting={showExisting} />
      ) : null}
      {editing ? <EditContactDialog contact={editing} onClose={() => setEditing(null)} /> : null}
      {windowContact ? (
        <ContactWindow contact={windowContact} onClose={() => setWindowId(null)} onRetired={onRetired} />
      ) : null}
      <BrokerBulkImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        onCommitted={async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['broker-firms'] }),
            queryClient.invalidateQueries({ queryKey: ['broker-contacts'] }),
            refreshAll(),
          ])
        }}
      />

      {/* Row 1 — search and actions. The lists live in the rail now; the row
          keeps what acts on the directory as a whole. The table's column and
          density controls appear only with the table they configure. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-400/10 bg-black/20 px-4 py-2">
        <label className="flex min-w-[220px] max-w-[520px] flex-1 items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1 focus-within:border-amber-400/30">
          <Search size={12} className="flex-shrink-0 text-stone-600" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            placeholder="Search name, company, email, phone, title…"
            aria-label="Search contacts"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              // Escape in the box clears the box first; a second Escape
              // reaches the panel.
              if (event.key === 'Escape' && search) {
                event.preventDefault()
                setSearch('')
              }
            }}
            className="min-w-0 flex-1 bg-transparent text-[11px] text-stone-300 outline-none placeholder:text-stone-600"
          />
          <kbd className="hidden flex-shrink-0 rounded border border-stone-700 px-1 font-mono text-[9px] text-stone-600 md:inline">/</kbd>
        </label>
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
          {layout === 'table' && !inActivity ? (
            <>
              <ColumnPicker selected={tableColumns} onChange={setTableColumns} />
              <DensityToggle density={density} onChange={setDensity} />
            </>
          ) : null}
          <button
            type="button"
            onClick={() => capture.mutate()}
            disabled={capture.isPending}
            title={CAPTURE_TOOLTIP}
            className={TOOLBAR_BUTTON}
          >
            <RefreshCw size={12} className={capture.isPending ? 'animate-spin motion-reduce:animate-none' : undefined} />
            {capture.isPending ? 'Reading deals…' : 'Re-read deals'}
          </button>
          <button
            type="button"
            onClick={() => setParam('view', inActivity ? null : 'activity', false)}
            aria-pressed={inActivity}
            title="What capture, dedup and enrichment have been doing"
            className={clsx(
              'flex items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
              inActivity
                ? 'border-amber-400/30 bg-amber-400/[0.10] text-amber-200'
                : 'border-amber-400/10 bg-black/40 text-stone-300 hover:text-amber-200',
            )}
          >
            <Sparkles size={12} />
            Smart Enrichment
          </button>
          <button
            type="button"
            onClick={() => setImporting(true)}
            title="Import a roster of brokerage firms and their contacts"
            className={TOOLBAR_BUTTON}
          >
            <Upload size={12} />
            Import
          </button>
          <button
            type="button"
            onClick={() => setAddingContact(true)}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-amber-400/20 bg-amber-400/[0.06] px-2.5 py-1 text-[11px] font-medium text-amber-300 transition-colors hover:bg-amber-400/[0.12] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
          >
            <Plus size={12} />
            Add Contact
          </button>
        </div>
      </div>

      {/* The lender's own people cannot be told apart from outside parties
          until identity suppression lists their email domains or names. The
          directory would otherwise look complete while filing the desk's
          originators and intake mailbox as brokers and counterparties. */}
      {contactQuery.data?.identitySuppression?.staffConfigured === false ? (
        <div
          role="note"
          aria-label="Identity suppression not configured"
          className="flex items-start gap-2 border-b border-amber-400/20 bg-amber-400/[0.06] px-4 py-1.5 text-[11px] text-amber-200"
        >
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>
            Identity suppression is not configured, so your own staff and intake mailbox are recorded here — and on every
            deal&apos;s Parties tab — as outside parties (brokers, &ldquo;Other party&rdquo;). List your firm&apos;s email
            domains and people under{' '}
            <Link to="/settings" className="underline decoration-dotted underline-offset-2 hover:text-amber-100">
              Settings → Platform Settings → Tenant Identity Suppression
            </Link>
            , then use Re-read deals.
          </span>
        </div>
      ) : null}

      {/* What the sweep actually read, rather than a spinner that stops. Below
          the row, so the row's geometry never moves. */}
      {captureNote || focusNote ? (
        <div className="flex items-center gap-3 border-b border-amber-400/10 bg-black/10 px-4 py-1 text-[10px]">
          {captureNote ? (
            <span role="status" className={captureNote.tone === 'error' ? 'text-red-300' : 'text-stone-400'}>
              {captureNote.text}
            </span>
          ) : null}
          {focusNote ? (
            <span role="status" className="text-amber-200/80">
              {focusNote}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setCaptureNote(null)
              setFocusNote(null)
            }}
            aria-label="Dismiss"
            className="ml-auto text-stone-500 hover:text-stone-300"
          >
            ×
          </button>
        </div>
      ) : null}

      {inActivity ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center gap-2 border-b border-amber-400/10 bg-black/10 px-4 py-1.5">
            <button
              type="button"
              onClick={() => setParam('view', null, false)}
              className="flex items-center gap-1 rounded-md border border-amber-400/10 bg-black/40 px-2 py-0.5 text-[10px] text-stone-300 hover:text-amber-200"
            >
              <ArrowLeft size={10} /> Back to directory
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
            <ActivityView
              contacts={contacts}
              onOpenContact={(id) => {
                setParam('view', null, false)
                showExisting(id)
              }}
            />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
          <SmartListRail
            lists={relationships.data?.lists}
            loading={relationships.isLoading}
            failed={relationships.isError}
            onRetry={() => relationships.refetch()}
            activeSmart={activeSmart}
            onSelectSmart={(key) => selectSmartList(key === activeSmart ? null : key)}
            activeListId={activeListId}
            onSelectList={selectSavedList}
            totalCount={contactQuery.isSuccess ? contacts.length : null}
          />

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 px-4 py-3 md:overflow-y-auto">
            {todayCollapsed ? null : (
            <TodayStrip
              narrow={layout === 'board' && Boolean(selected) && !windowContact}
              goingQuiet={smartCount('cooling')}
              coolingRule={relationships.data?.definitions.stages.find((s) => s.key === 'cooling')?.rule}
              relationshipsFailed={relationships.isError}
              onOpenGoingQuiet={() => openSmartList('cooling')}
              duplicates={duplicates.isSuccess ? (duplicates.data.candidates?.length ?? 0) : null}
              duplicatesFailed={duplicates.isError}
              onReviewDuplicates={() => setParam('view', 'activity', false)}
              enrichment={enrichment}
              toConfirm={smartCount('confirm')}
              onOpenToConfirm={() => openSmartList('confirm')}
            />
            )}

            {/* Toolbar — what is being looked at, the type chips, the switch. */}
            <section aria-label="View controls" className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="mr-auto flex min-w-0 flex-wrap items-baseline gap-x-3">
                <h1 className="font-display text-[28px] font-semibold leading-none text-stone-50">{scopeName ?? 'All contacts'}</h1>
                <span className="text-[11px] text-stone-500" aria-live="polite">
                  {contactQuery.isSuccess && listReady ? shownText : 'Reading the directory…'}
                </span>
              </div>
              <div role="group" aria-label="Contact type" className="flex flex-wrap gap-1.5">
                {TYPE_CHIPS.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    aria-pressed={typeChip === chip.key}
                    onClick={() => setTypeChip(chip.key)}
                    className={clsx(
                      'rounded-full border px-3 py-1 text-[11px] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
                      typeChip === chip.key
                        ? 'border-amber-400/40 bg-amber-400/[0.10] text-amber-200'
                        : 'border-white/10 text-stone-400 hover:text-stone-200',
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <label className="flex flex-shrink-0 items-center gap-1.5 text-[10px] text-stone-400" title="Show contacts that have been retired">
                <input
                  type="checkbox"
                  checked={includeRetired}
                  onChange={(event) => setIncludeRetired(event.target.checked)}
                  className="h-3 w-3 accent-amber-400"
                />
                Include retired
              </label>
              <button
                type="button"
                aria-expanded={!todayCollapsed}
                aria-controls="crm-today"
                onClick={() => setTodayCollapsed(!todayCollapsed)}
                title={todayCollapsed ? 'Show the Today strip' : 'Hide the Today strip to give the list more room'}
                className="rounded-md border border-white/10 px-2.5 py-1 text-[11px] text-stone-400 hover:text-stone-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
              >
                Today {todayCollapsed ? '▸' : '▾'}
              </button>
              <div role="group" aria-label="View" className="inline-flex overflow-hidden rounded-md border border-amber-400/15">
                {(['table', 'board'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={layout === option}
                    onClick={() => changeLayout(option)}
                    className={clsx(
                      'px-3.5 py-1 text-[11px] font-medium focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-amber-400/60',
                      layout === option ? 'bg-amber-400/15 text-amber-100' : 'text-stone-400 hover:text-stone-200',
                    )}
                  >
                    {option === 'table' ? 'Table' : 'Board'}
                  </button>
                ))}
              </div>
            </section>

            {layout === 'table' ? (
              <>
                {/* The stat strip: each tile counts a subset of the current
                    list/search scope AND filters the table to it when clicked.
                    One line per tile rather than the stacked metric-cell. */}
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                  {stats.map((m) => {
                    const active = statFilter === m.key
                    const ready = contactQuery.isSuccess && listReady
                    return (
                      <button
                        key={m.key}
                        type="button"
                        aria-pressed={active}
                        title={active ? `Showing ${m.label.toLowerCase()} — click to clear` : `Show only: ${m.label.toLowerCase()}`}
                        onClick={() => setStatFilter(active || m.key === 'all' ? null : m.key)}
                        className={clsx(
                          'flex h-7 items-center justify-between gap-2 rounded border px-2 text-left transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
                          active
                            ? 'border-amber-400/40 bg-amber-400/[0.10]'
                            : 'border-white/[0.06] bg-white/[0.02] hover:border-amber-400/25 hover:bg-amber-400/[0.05]',
                        )}
                      >
                        <span
                          className={clsx(
                            'truncate text-[9px] uppercase tracking-[0.14em]',
                            active ? 'text-amber-200' : 'text-stone-500',
                          )}
                        >
                          {m.label}
                        </span>
                        <span
                          className={clsx(
                            'font-mono text-[13px] leading-none',
                            active ? 'text-amber-200' : 'text-stone-200',
                          )}
                        >
                          {ready ? m.value : '…'}
                        </span>
                      </button>
                    )
                  })}
                </div>

                {/* Body — the table, and the panel beside it once a row is chosen. */}
                <div className="flex min-h-[360px] flex-1 overflow-hidden rounded-lg border border-white/[0.06]">
                  <div className={clsx('flex min-h-0 min-w-0 flex-col', selected && !windowContact ? 'w-[58%]' : 'w-full')}>
                    {contactQuery.isError ? (
                      <div className="flex items-center gap-3 px-4 py-3 text-[11px] text-red-300" role="alert">
                        The directory could not be read.
                        <button
                          type="button"
                          onClick={() => contactQuery.refetch()}
                          className="rounded border border-red-400/30 px-2 py-0.5 text-[10px] text-red-200 hover:bg-red-400/10"
                        >
                          Retry
                        </button>
                      </div>
                    ) : membersFailed ? (
                      <div className="flex items-center gap-3 px-4 py-3 text-[11px] text-red-300" role="alert">
                        The list's members could not be read.
                        <button
                          type="button"
                          onClick={() => members.refetch()}
                          className="rounded border border-red-400/30 px-2 py-0.5 text-[10px] text-red-200 hover:bg-red-400/10"
                        >
                          Retry
                        </button>
                      </div>
                    ) : smartFailed ? (
                      <div className="flex items-center gap-3 px-4 py-3 text-[11px] text-red-300" role="alert">
                        The built-in list could not be read, so it cannot be applied.
                        <button
                          type="button"
                          onClick={() => relationships.refetch()}
                          className="rounded border border-red-400/30 px-2 py-0.5 text-[10px] text-red-200 hover:bg-red-400/10"
                        >
                          Retry
                        </button>
                      </div>
                    ) : (
                      <ContactTable
                        columns={tableColumns}
                        onColumnsChange={setTableColumns}
                        density={density}
                        contacts={filtered}
                        relationships={relationshipRows}
                        loading={contactQuery.isLoading || !listReady}
                        selectedId={selectedId}
                        onSelect={selectContact}
                        onOpen={openWindow}
                        onEdit={setEditing}
                        onRetired={onRetired}
                        emptyMessage={emptyMessage}
                      />
                    )}
                  </div>
                  {selected && !windowContact ? (
                    <ContactProfilePanel
                      key={selected.id}
                      contact={selected}
                      contacts={contacts}
                      onClose={() => setSelectedId(null)}
                      onOpenWindow={openWindow}
                      onEdit={setEditing}
                      onRetired={onRetired}
                      onSelectContact={showExisting}
                    />
                  ) : null}
                </div>
              </>
            ) : (
              <div className="min-h-[360px] flex-1 md:overflow-y-auto">
                {contactQuery.isError ? (
                  <div className="flex items-center gap-3 px-1 py-3 text-[11px] text-red-300" role="alert">
                    The directory could not be read.
                    <button
                      type="button"
                      onClick={() => contactQuery.refetch()}
                      className="rounded border border-red-400/30 px-2 py-0.5 text-[10px] text-red-200 hover:bg-red-400/10"
                    >
                      Retry
                    </button>
                  </div>
                ) : relationships.isError || membersFailed ? (
                  <div className="flex items-center gap-3 px-1 py-3 text-[11px] text-red-300" role="alert">
                    {membersFailed
                      ? "The list's members could not be read."
                      : 'The relationship board could not be read. The Table still shows the directory.'}
                    <button
                      type="button"
                      onClick={() => (membersFailed ? members.refetch() : relationships.refetch())}
                      className="rounded border border-red-400/30 px-2 py-0.5 text-[10px] text-red-200 hover:bg-red-400/10"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <RelationshipBoard
                    entries={boardEntries}
                    definitions={relationships.data?.definitions}
                    selectedId={selectedId}
                    onSelect={(id) => selectContact(id === selectedId ? null : id)}
                    onOpen={openWindow}
                    loading={contactQuery.isLoading || relationships.isLoading || !listReady}
                    emptyMessage={boardEmptyMessage}
                  />
                )}
              </div>
            )}
          </div>

          {layout === 'board' && selected && !windowContact ? (
            <RelationshipPeek
              key={selected.id}
              contact={selected}
              row={relationshipRows.get(selected.id)}
              definitions={relationships.data?.definitions}
              onClose={() => setSelectedId(null)}
              onOpenProfile={openWindow}
              onEdit={setEditing}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

